import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, Plus, Trash2, Save, Edit3, X, ArrowUp, ArrowDown, MessageSquare } from "lucide-react";

/** Feature 2 — Legal Admin / Ops Admin template editor for "Message this
 *  person". Templates live in system_settings.message_templates and are used
 *  by MessagePersonModal on the People Strip. */
const CAN_EDIT_ROLES = ["legal_manager", "general_counsel", "it_admin"];

const PLACEHOLDERS = [
  { tok: "{{cn}}",     desc: "Contract number" },
  { tok: "{{vendor}}", desc: "Vendor legal name" },
  { tok: "{{stage}}",  desc: "Current stage" },
  { tok: "{{first}}",  desc: "Recipient first name" },
  { tok: "{{me}}",     desc: "Sender name (blank to fill in)" },
];

const BLANK = { id: null, label: "", subject: "", body: "" };

export default function MessageTemplatesAdmin() {
  const { user } = useAuth();
  const canEdit = CAN_EDIT_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // template obj or null
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/message-templates")
    .then(r => setItems(r.data.items || []))
    .catch(() => toast.error("Failed to load templates"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.label?.trim() || !editing?.subject?.trim() || !editing?.body?.trim()) {
      return toast.error("Label, subject and body are required");
    }
    setBusy(true);
    try {
      const payload = {
        label: editing.label.trim(),
        subject: editing.subject.trim(),
        body: editing.body.trim(),
      };
      if (editing.id) {
        await api.put(`/settings/message-templates/${editing.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post(`/settings/message-templates`, payload);
        toast.success("Template created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.label}"?`)) return;
    try {
      await api.delete(`/settings/message-templates/${tpl.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await api.post(`/settings/message-templates/reorder`, { ids: next.map(x => x.id) });
    } catch (e) { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="msg-template-admin">
      <div className="flex items-center gap-3 mb-5">
        <Mail className="w-5 h-5 text-[#005289]" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Legal Admin · Communication</div>
          <h1 className="text-2xl font-bold text-slate-900">Message Person Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Reusable email templates for the People Strip "Message" button. Placeholders auto-fill on send.</p>
        </div>
        {canEdit && (
          <button onClick={() => setEditing({ ...BLANK })}
                  data-testid="tpl-new-btn"
                  className="ml-auto h-9 px-3 text-xs font-semibold rounded bg-[#00AE42] text-white flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> New template
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Available placeholders</div>
        <div className="flex flex-wrap gap-2">
          {PLACEHOLDERS.map(p => (
            <span key={p.tok} className="inline-flex items-center gap-2 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <code className="font-mono font-bold text-[#005289]">{p.tok}</code>
              <span className="text-slate-500">— {p.desc}</span>
            </span>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-10 border border-dashed border-slate-300 rounded-md">
          No templates yet. Click "New template" to add one.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((t, idx) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-md p-4"
                 data-testid={`tpl-row-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 pt-1">
                  <button disabled={!canEdit || idx === 0} onClick={() => move(idx, -1)}
                          data-testid={`tpl-up-${t.id}`}
                          className="w-6 h-6 flex items-center justify-center rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30">
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button disabled={!canEdit || idx === items.length - 1} onClick={() => move(idx, 1)}
                          data-testid={`tpl-down-${t.id}`}
                          className="w-6 h-6 flex items-center justify-center rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30">
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-bold text-sm text-slate-900">{t.label}</div>
                    <span className="text-[10px] font-mono text-slate-400">#{t.id}</span>
                  </div>
                  <div className="text-xs text-slate-700 mb-1"><b>Subject:</b> {t.subject}</div>
                  <pre className="text-[11px] text-slate-600 bg-slate-50 border border-slate-100 rounded p-2 whitespace-pre-wrap max-h-32 overflow-auto font-sans">{t.body}</pre>
                </div>
                {canEdit && (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditing({ ...t })}
                            data-testid={`tpl-edit-${t.id}`}
                            className="h-8 px-2 text-[11px] border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => del(t)}
                            data-testid={`tpl-delete-${t.id}`}
                            className="h-8 px-2 text-[11px] border border-red-200 text-red-600 rounded hover:bg-red-50 flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}
             data-testid="tpl-edit-modal">
          <div className="bg-white rounded-md w-full max-w-2xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#005289]" />
              <div className="font-bold text-sm text-slate-900">{editing.id ? "Edit template" : "New template"}</div>
              <button onClick={() => setEditing(null)} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-xs block">
                <span className="font-bold text-slate-700 block mb-1">Label</span>
                <input value={editing.label} onChange={e => setEditing({ ...editing, label: e.target.value })}
                       data-testid="tpl-edit-label" maxLength={80}
                       placeholder="e.g., Contract update"
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" />
              </label>
              <label className="text-xs block">
                <span className="font-bold text-slate-700 block mb-1">Subject</span>
                <input value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })}
                       data-testid="tpl-edit-subject" maxLength={160}
                       placeholder="e.g., Contract #{{cn}} — status update"
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md" />
              </label>
              <label className="text-xs block">
                <span className="font-bold text-slate-700 block mb-1">Body</span>
                <textarea value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })}
                          data-testid="tpl-edit-body" rows={10} maxLength={8000}
                          placeholder="Hi {{first}}, ..."
                          className="w-full p-3 text-sm border border-slate-200 rounded-md resize-none focus:border-[#005289] focus:outline-none font-mono" />
              </label>
              <div className="text-[10px] text-slate-500">
                Placeholders: {PLACEHOLDERS.map(p => <code key={p.tok} className="mx-1 font-mono">{p.tok}</code>)}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
              <button onClick={() => setEditing(null)} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="tpl-save-btn"
                      className="ml-auto h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-50">
                <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
